import { Image } from "@/components/display";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { formatNumber, formatString } from "@/shared/formats";
import { Articulo } from "@/shared/types/articulo";
import { Stack, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useGetArticuloDepositosQuery } from "@/features/inventario/articulos/api/get-articulo-depositos";

export default function ViewArticulo({ articulo }: { articulo: Articulo }) {

    const { monedaBase } = useMonetizacion();
    const { data: depositos } = useGetArticuloDepositosQuery(articulo.id);

    return <Stack flexWrap={"wrap"} direction={"row"} padding={3} gap={5}>
        {/*Imagen*/}
        <Stack minWidth={"200px"} flex={1} gap={2} alignItems={"center"}>
            <Image maxWidth={"300px"} width={"100%"} src={articulo.imagen} />
        </Stack>
        <Stack minWidth={"300px"} flex={2} gap={3} >
            {/*Titutlo*/}
            <Stack gap={2}>
                <Stack gap={1}>
                    <Typography variant="h4">{formatString(articulo.nombre)}</Typography>
                    <Typography variant="subtitle1" color="text.secondary">{formatString(articulo.rubroNombre)}</Typography>
                </Stack>
                {articulo.descripcion ?
                    <Typography>{articulo.descripcion}</Typography> :
                    <Typography color="text.secondary">Sin descripcion</Typography>
                }
            </Stack>

            <Stack columnGap={5} rowGap={3} flexDirection={"row"} flexWrap={"wrap"}>
                {/*Costo Precio*/}
                <Stack gap={1} >
                    <Typography variant="h6">Costo/Precios</Typography>
                    <Stack gap={1} marginLeft={3}>
                        <AttributeValuePair label={"Costo: "} value={`${formatNumber(articulo.costo)} ${monedaBase.simbolo}`} />
                        <AttributeValuePair label={"IVA: "} value={articulo.iva == 0 ? "Exenta" : `${articulo.iva}%`} />
                        <Typography variant="subtitle1">Precios</Typography>
                        <Stack marginLeft={3}>
                            {articulo.precios.length <= 0 ?
                                <Typography color="text.secondary">Sin Precios</Typography> :
                                articulo.precios.map((p, i) => (
                                    <AttributeValuePair key={i} label={`Precio ${i + 1}: `} value={`${formatNumber(p)} ${monedaBase.simbolo}`} />
                                ))
                            }
                        </Stack>
                    </Stack>
                </Stack>

                {/*Inventario*/}
                <Stack gap={1} >
                    <Typography variant="h6">Datos de Inventario</Typography>
                    <Stack gap={1} marginLeft={3}>
                        <AttributeValuePair label={"Código: "} value={articulo.codigo} />
                        <AttributeValuePair label={"Número: "} value={articulo.numeroItem} />
                        <AttributeValuePair label={"Cantidad: "} value={formatNumber(articulo.cantidad)} />
                        <AttributeValuePair label={"Cantidad Mínima: "} value={formatNumber(articulo.cantidadMinima)} />
                        <AttributeValuePair label={"Cantidad por Caja: "} value={formatNumber(articulo.cantidadPorCaja)} />
                    </Stack>
                </Stack>

                {/*Stock por Depósito*/}
                <Stack gap={1} >
                    <Typography variant="h6">Stock por Depósito</Typography>
                    <Stack gap={1} marginLeft={3}>
                        {!depositos || depositos.length <= 0 ?
                            <Typography color="text.secondary">Sin depósitos</Typography> :
                            depositos.map(d => (
                                <AttributeValuePair
                                    key={d.id}
                                    label={`${d.depositoNombre}${d.depositoPrincipal ? " (Principal)" : ""}: `}
                                    value={formatNumber(d.cantidad)}
                                />
                            ))
                        }
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    </Stack>
}

function AttributeValuePair({
    label,
    value
}: {
    label: ReactNode,
    value: ReactNode
}) {
    return <Stack direction={"row"} gap={2}>
        <Typography variant="body1">{label}</Typography>
        <Typography variant="body1">{formatString(value)}</Typography>
    </Stack>
}
